import React, { useState, useEffect } from 'react';
import { Settings, Server, Globe, ArrowRight, Activity, AlertTriangle, CheckCircle, RefreshCw, Plus } from 'lucide-react';
import { toast } from 'react-toastify';

const ProxyConfiguration = () => {
  const [proxyStatus, setProxyStatus] = useState(null);
  const [proxyRules, setProxyRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restarting, setRestarting] = useState(false);
  const [reloading, setReloading] = useState(false);
  
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState({ path: '', target: '' });
  const [addingRule, setAddingRule] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': case 'active': return 'text-green-400';
      case 'stopped': case 'inactive': return 'text-red-400';
      case 'installed': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'stopped': case 'inactive': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  // API functions
  const fetchProxyStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setProxyStatus(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch proxy status');
      console.error('Error fetching proxy status:', err);
    }
  };

  const fetchProxyRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/rules', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setProxyRules(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch proxy rules');
      console.error('Error fetching proxy rules:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchProxyStatus(), fetchProxyRules()]);
    } finally {
      setLoading(false);
    }
  };

  const restartApache = async () => {
    setRestarting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/apache/restart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Apache restarted successfully');
        await loadData();
      } else {
        toast.error(result.message || 'Failed to restart Apache');
      }
    } catch (err) {
      toast.error('Failed to restart Apache');
      console.error('Error restarting Apache:', err);
    } finally {
      setRestarting(false);
    }
  };

  const reloadConfig = async () => {
    setReloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/apache/reload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Configuration reloaded successfully');
        await loadData();
      } else {
        toast.error(result.message || 'Failed to reload configuration');
      }
    } catch (err) {
      toast.error('Failed to reload configuration');
      console.error('Error reloading config:', err);
    } finally {
      setReloading(false);
    }
  };

  const addProxyRule = async () => {
    if (!newRule.path || !newRule.target) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingRule(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/rules', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRule)
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Proxy rule added successfully');
        setNewRule({ path: '', target: '' });
        setShowAddRule(false);
        await loadData();
      } else {
        toast.error(result.message || 'Failed to add proxy rule');
      }
    } catch (err) {
      toast.error('Failed to add proxy rule');
      console.error('Error adding proxy rule:', err);
    } finally {
      setAddingRule(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading proxy configuration...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Error loading proxy configuration</span>
        </div>
        <p className="text-red-300 mt-2">{error}</p>
        <button 
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Proxy Server Status */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center">
            <Server className="w-6 h-6 text-blue-400 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-slate-200">Proxy Server Status</h2>
              <p className="text-slate-400 mt-1">Monitor and manage proxy server instances</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Apache2 Status */}
            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-slate-200">Apache2</h3>
                <div className={`flex items-center ${getStatusColor(proxyStatus?.apache?.status)}`}>
                  {getStatusIcon(proxyStatus?.apache?.status)}
                  <span className="ml-2 text-sm font-medium capitalize">{proxyStatus?.apache?.status || 'Unknown'}</span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">HTTP Port:</span>
                  <span className="text-slate-200">{proxyStatus?.apache?.port || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">HTTPS Port:</span>
                  <span className="text-slate-200">{proxyStatus?.apache?.sslPort || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">PID:</span>
                  <span className="text-slate-200">{proxyStatus?.apache?.pid || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Nginx Status */}
            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-slate-200">Nginx</h3>
                <div className={`flex items-center ${getStatusColor(proxyStatus?.nginx?.status)}`}>
                  {getStatusIcon(proxyStatus?.nginx?.status)}
                  <span className="ml-2 text-sm font-medium capitalize">{proxyStatus?.nginx?.status || 'Unknown'}</span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">HTTP Port:</span>
                  <span className="text-slate-200">{proxyStatus?.nginx?.port || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">PID:</span>
                  <span className="text-slate-200">{proxyStatus?.nginx?.pid || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Proxy Rules */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Globe className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">Active Proxy Rules</h2>
                <p className="text-slate-400 mt-1">Current routing configuration from ksp3plhq.com</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAddRule(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {proxyRules.map(rule => (
              <div key={rule.id} className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center text-slate-300">
                      <span className="text-blue-400 font-mono">{rule.path}</span>
                      <ArrowRight className="w-4 h-4 mx-3 text-slate-500" />
                      <span className="font-mono text-sm">{rule.target}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center ${getStatusColor(rule.status)}`}>
                      {getStatusIcon(rule.status)}
                      <span className="ml-1 text-sm capitalize">{rule.status}</span>
                    </div>
                    <button className="text-slate-400 hover:text-slate-200 text-sm">
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center">
            <Settings className="w-6 h-6 text-blue-400 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-slate-200">Quick Actions</h2>
              <p className="text-slate-400 mt-1">Common proxy management tasks</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={restartApache}
              disabled={restarting}
              className="p-4 bg-slate-900 border border-slate-600 rounded-lg hover:bg-slate-700 text-left disabled:opacity-50"
            >
              <h3 className="font-medium text-slate-200 mb-1">
                {restarting ? 'Restarting...' : 'Restart Apache'}
              </h3>
              <p className="text-sm text-slate-400">Restart the Apache proxy server</p>
            </button>
            <button 
              onClick={reloadConfig}
              disabled={reloading}
              className="p-4 bg-slate-900 border border-slate-600 rounded-lg hover:bg-slate-700 text-left disabled:opacity-50"
            >
              <h3 className="font-medium text-slate-200 mb-1">
                {reloading ? 'Reloading...' : 'Reload Config'}
              </h3>
              <p className="text-sm text-slate-400">Reload proxy configuration</p>
            </button>
            <button className="p-4 bg-slate-900 border border-slate-600 rounded-lg hover:bg-slate-700 text-left">
              <h3 className="font-medium text-slate-200 mb-1">View Logs</h3>
              <p className="text-sm text-slate-400">Check proxy access logs</p>
            </button>
          </div>
        </div>
      </div>

      {/* Add Rule Modal */}
      {showAddRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-200 mb-4">Add Proxy Rule</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Source Path
                </label>
                <input
                  type="text"
                  value={newRule.path}
                  onChange={(e) => setNewRule({...newRule, path: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., /api/myservice"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Target URL
                </label>
                <input
                  type="text"
                  value={newRule.target}
                  onChange={(e) => setNewRule({...newRule, target: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., http://localhost:3000/api/myservice"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddRule(false)}
                className="px-4 py-2 text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={addProxyRule}
                disabled={addingRule}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {addingRule ? 'Adding...' : 'Add Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProxyConfiguration;